import cx from 'classnames';
import {filter, flatten, trim} from 'lodash';
import {Fragment, useCallback, useMemo, useState} from 'react';

import {Icon, IconName, IconProps} from 'components';

import {FormControl, FormControlProps} from '../FormControl';

type Props = {
    value: string;
    name?: string;
    type?: string;
    autoComplete?: string;
    autoFocus?: boolean;
    transparent?: boolean;
    hasEndIcon?: boolean;
    label?: string;
    pattern?: string;
    placeholder?: string;
    styled?: boolean;
    maxLength?: number;
    icon?: IconName;
    iconProps?: Partial<IconProps>;
    fontLarge?: boolean;
    readonly?: boolean;
    inputClassName?: string;
    onChange?: (value: string, originalEvent?: React.ChangeEvent<HTMLInputElement>) => void;
} & Partial<FormControlProps>;

export type InputProps = Props;

export const Input: React.FC<Props> = ({
    name,
    description,
    className,
    label,
    pattern,
    placeholder,
    autoComplete,
    autoFocus,
    active,
    transparent,
    hasEndIcon,
    required,
    disabled,
    skipErrorMessage,
    hasError,
    errorMessage,
    styled = true,
    validate = true,
    readonly = false,
    value = '',
    type = 'text',
    maxLength,
    icon,
    iconProps,
    fontLarge,
    spacing,
    inputClassName,
    onBlur: onBlurProp,
    onChange
}: Props) => {
    const [initialValue] = useState<string | number>(value);

    const isDirty = useMemo<boolean>(() => value !== initialValue, [initialValue, value]);

    const shouldTrim = useMemo<boolean>(() => ['text', 'email'].includes(type), [type]);

    const onBlurHandler = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (shouldTrim) {
            const rawValue = e.currentTarget.value;
            const newValue = trim(rawValue);
            if (rawValue !== newValue) {
                onChange?.(newValue, e);
            }
        }
    }, [shouldTrim, onChange]);

    const onChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.currentTarget.value;
        const newValue = (rawValue && shouldTrim) ? rawValue.trimStart() : rawValue;
        onChange?.(newValue, e);
    }, [shouldTrim, onChange]);

    const mergedDescription = useMemo(() => {
        const maxLengthDescription = maxLength ? `${value?.length || 0} / ${maxLength}` : null;
        return filter(flatten([maxLengthDescription, description]));
    }, [maxLength, value, description]);

    return (
        <FormControl {...{
            value, label, description: mergedDescription, autoComplete, className, styled,
            required, disabled, skipErrorMessage, hasError, errorMessage,
            dirty: isDirty, onBlur: onBlurProp, validate, fontLarge, spacing, active
        }}
        >
            {({onFocus, onBlur}) => (
                <Fragment>
                    <input
                        className={cx(transparent ? 'tw-bg-transparent' : 'tw-bg-light', inputClassName, {
                            'tw-w-full tw-py-1 tw-px-3 tw-h-8': styled,
                            'tw-pr-10': hasEndIcon
                        })}
                        type={type}
                        disabled={disabled}
                        readOnly={readonly}
                        autoFocus={autoFocus}
                        autoComplete={autoComplete}
                        name={name}
                        pattern={pattern}
                        placeholder={placeholder}
                        value={value}
                        maxLength={maxLength}
                        onBlur={(e) => {
                            onBlur(e);
                            onBlurHandler(e);
                        }}
                        onChange={onChangeHandler}
                        onFocus={onFocus}
                    />
                    {icon &&
                        <Icon
                            name={icon}
                            className='tw-my-px tw-h-full tw-absolute tw-right-3'
                            color='default'
                            size='sm'
                            button
                            {...iconProps}
                        />}
                </Fragment>
            )}
        </FormControl>
    );
};
